import { useCallback, useState, type FormEvent, type ReactElement } from 'react';
import { darkTheme } from '../lib/tokens';

// Ersatz für window.prompt / window.confirm.
//
// iOS-Safari unterdrückt die nativen Dialoge in vielen Situationen (Home-Screen-
// PWA, „Weitere Dialoge unterdrücken", Tab im Hintergrund). prompt() liefert
// dann sofort null zurück — für den Admin sieht es so aus, als würde der Button
// gar nichts tun. Darum rendern wir eigene Modals statt der nativen Dialoge.
//
// Aufruf:
//   const dialogs = useDialogs();
//   const reason = await dialogs.prompt({ title: 'Sperren', label: 'Grund' });
//   if (!reason) return;
//   ...
//   {dialogs.dialog}   // in den Render-Tree einsetzen

export interface PromptSpec {
  title: string;
  message?: string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  confirmLabel?: string;
  destructive?: boolean;
}

export interface ConfirmSpec {
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
}

interface UseDialogs {
  // Resolved mit dem getrimmten Text, oder null bei Abbruch.
  prompt: (spec: PromptSpec) => Promise<string | null>;
  // Resolved mit true/false.
  confirm: (spec: ConfirmSpec) => Promise<boolean>;
  dialog: ReactElement | null;
}

type Active =
  | { kind: 'prompt'; spec: PromptSpec; resolve: (value: string | null) => void }
  | { kind: 'confirm'; spec: ConfirmSpec; resolve: (value: boolean) => void };

export function useDialogs(): UseDialogs {
  const [active, setActive] = useState<Active | null>(null);

  const prompt = useCallback((spec: PromptSpec): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      setActive({ kind: 'prompt', spec, resolve });
    });
  }, []);

  const confirm = useCallback((spec: ConfirmSpec): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setActive({ kind: 'confirm', spec, resolve });
    });
  }, []);

  let dialog: ReactElement | null = null;
  if (active?.kind === 'prompt') {
    dialog = (
      <PromptDialog
        spec={active.spec}
        onSubmit={(value) => {
          active.resolve(value);
          setActive(null);
        }}
        onCancel={() => {
          active.resolve(null);
          setActive(null);
        }}
      />
    );
  } else if (active?.kind === 'confirm') {
    dialog = (
      <ConfirmDialog
        spec={active.spec}
        onConfirm={() => {
          active.resolve(true);
          setActive(null);
        }}
        onCancel={() => {
          active.resolve(false);
          setActive(null);
        }}
      />
    );
  }

  return { prompt, confirm, dialog };
}

function PromptDialog({
  spec,
  onSubmit,
  onCancel,
}: {
  spec: PromptSpec;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [value, setValue] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  }

  return (
    <ModalShell onSubmit={handleSubmit} title={spec.title}>
      {spec.message && <p className="mb-4 text-sm text-text-secondary">{spec.message}</p>}
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-text-secondary">{spec.label}</span>
        {spec.multiline ? (
          <textarea
            required
            autoFocus
            rows={3}
            value={value}
            placeholder={spec.placeholder}
            onChange={(e) => setValue(e.target.value)}
            className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          />
        ) : (
          <input
            type="text"
            required
            autoFocus
            value={value}
            placeholder={spec.placeholder}
            onChange={(e) => setValue(e.target.value)}
            className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          />
        )}
      </label>

      <ModalActions
        onCancel={onCancel}
        confirmLabel={spec.confirmLabel ?? 'Bestätigen'}
        destructive={spec.destructive}
        disabled={!value.trim()}
      />
    </ModalShell>
  );
}

function ConfirmDialog({
  spec,
  onConfirm,
  onCancel,
}: {
  spec: ConfirmSpec;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <ModalShell
      onSubmit={(e) => {
        e.preventDefault();
        onConfirm();
      }}
      title={spec.title}
    >
      <p className="text-sm text-text-secondary">{spec.message}</p>
      <ModalActions
        onCancel={onCancel}
        confirmLabel={spec.confirmLabel ?? 'Bestätigen'}
        destructive={spec.destructive}
      />
    </ModalShell>
  );
}

// ── Gemeinsame Bausteine (auch von SubscriptionOverrideModal genutzt) ─────

export function ModalShell({
  title,
  onSubmit,
  children,
}: {
  title: string;
  onSubmit: (e: FormEvent) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-4">
      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="my-auto w-full max-w-[400px] rounded-2xl border p-6 shadow-2xl"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <h2 className="mb-4 text-base font-semibold text-text-primary">{title}</h2>
        {children}
      </form>
    </div>
  );
}

export function ModalActions({
  onCancel,
  confirmLabel,
  destructive,
  disabled,
}: {
  onCancel: () => void;
  confirmLabel: string;
  destructive?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="mt-5 flex items-center gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-bg-elevated"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        disabled={disabled}
        className="flex-1 rounded-md px-3 py-2 text-sm font-semibold text-white transition disabled:opacity-50"
        style={{
          background: destructive ? darkTheme.colors.danger : darkTheme.colors.accent,
        }}
      >
        {confirmLabel}
      </button>
    </div>
  );
}
